/**
 * Core de comunicação com o CorelDraw via WebView2 (Refatorado)
 *
 * Esta é a versão modularizada que delega responsabilidades para managers especializados.
 *
 * @module CorelCommunication
 */

import { WebViewMessage, WebViewResponse } from "../../types/messages";
import { DEFAULT_REQUEST_TIMEOUT_MS, ACTION_TIMEOUTS } from "../../constants";
import { timeoutService } from "../../timeoutService";
import { RequestTracker } from "../../requestTracker";
import { MessageRouter } from "../../messageRouter";
import { SessionManager } from "../../sessionManager";
import { bootstrapWithFallback, type BootstrapData } from "../../bootstrap";
import { sendToTerminal } from "../../../terminalLogger";
import { LogCategory } from "@/config/logging";
import { TokenValidator } from "../../tokenValidator";
import {
	getToolsSessionReadyState,
	isToolsDockerContext,
	shouldBypassToolsSessionGuard,
	waitForToolsSessionReady,
} from "../../toolsSession";

// Managers modulares
import { AuthManager } from "../auth";
import { MessageQueue, MessageSender, MessageValidator } from "../messaging";
import { EventManager } from "../events";
import { DevMockProvider } from "../dev";
import { LogManager } from "../logging";
import { hmacLogger } from "../logging/hmacLogger";
import { launcherReadiness } from "../launcherReadiness";

/**
 * Classe principal de comunicação (Orquestrador)
 *
 * Delega responsabilidades para managers especializados:
 * - AuthManager: HMAC e autenticação
 * - MessageQueue: Fila de mensagens pendentes
 * - MessageSender: Envio de mensagens
 * - MessageValidator: Validações
 * - EventManager: Eventos C# → Frontend
 * - DevMockProvider: Simulação em desenvolvimento
 * - LogManager: Logs estruturados
 */
export class CorelCommunication {
	private static instance: CorelCommunication;

	// Managers especializados
	private authManager: AuthManager;
	private messageQueue: MessageQueue;
	private messageSender: MessageSender;
	private messageValidator: MessageValidator;
	private eventManager: EventManager;
	private devMock: DevMockProvider;
	private logManager: LogManager;

	// Componentes existentes
	private tracker: RequestTracker;
	private router: MessageRouter;
	private session: SessionManager;

	// Estado interno
	private isListenerConfigured = false;
	private defaultCallback: ((response: WebViewResponse) => void) | null = null;
	private bootstrapInitialized = false; // Flag para rastrear se bootstrap inicial foi bem-sucedido
	private lastResponseAt: number | null = null; // Última resposta recebida (para health/status)
	private lastHealthSentAt: number | null = null; // Último health enviado automaticamente
	private lastTokenRotationLogAt: number = 0; // Throttle para log de rotação de token
	private static readonly TOKEN_ROTATION_LOG_THROTTLE_MS = 3000; // 3 segundos entre logs
	private static readonly TOOLS_SESSION_WAIT_TIMEOUT_MS = 5000;

	/**
	 * Valida se o sessionToken tem formato válido.
	 *
	 * ✅ DELEGADO para TokenValidator (centralizado)
	 *
	 * @deprecated Use TokenValidator.validateSessionToken() diretamente
	 */
	private isValidSessionToken(token: string | null | undefined): boolean {
		return TokenValidator.validateSessionToken(token).valid;
	}

	private constructor() {
		// Inicializar managers
		this.authManager = new AuthManager();
		this.messageQueue = new MessageQueue();
		this.messageSender = new MessageSender();
		this.messageValidator = new MessageValidator();
		this.eventManager = new EventManager();
		this.devMock = new DevMockProvider();
		this.logManager = new LogManager();

		// Inicializar componentes existentes
		this.tracker = new RequestTracker();
		this.router = new MessageRouter(this.eventManager); // Passar EventManager para MessageRouter
		this.session = new SessionManager();

		// 🔍 DEBUG: Expor tracker globalmente para diagnóstico no console
		// Uso: window.__corelTracker.logPendingRequests() ou window.__corelTracker.getPendingRequestsInfo()
		(window as any).__corelTracker = this.tracker;

		// Configurar session manager
		try {
			this.session.configure({
				onFlushQueue: () => this.flushPendingQueue(),
				// ⚠️ IMPORTANTE: Apenas actions que o backend C# também trata como isentas de sessionToken!
				// Conforme contrato do backend C# (05/12/2025):
				// - session:hello, tools:bootstrap-session: isentas de sessionToken
				// - app-status, health:pong e open-tools-docker: EXIGEM sessionToken
				tokenExemptActions: new Set([
					"session:hello",
					"tools:bootstrap-session",
					"tools:ready-for-session",
					"tools:session-applied",
				]),
			});
		} catch (error) {
			// Ignorar erros de configuração
		}

		// Registrar listener o quanto antes
		try {
			this.setupListenerInternal();
		} catch (error) {
			// Ignorar erros de configuração de listener
		}
	}

	/**
	 * Obtém a instância única (Singleton)
	 */
	public static getInstance(): CorelCommunication {
		if (!this.instance) {
			this.instance = new CorelCommunication();
		}
		return this.instance;
	}

	/**
	 * Define o token de sessão e dispara flush da fila
	 */
	public setSessionToken(token: string | null | undefined): void {
		const normalized = token == null ? null : String(token);
		this.session.setSessionToken(normalized);

		if (normalized) {
			this.flushPendingQueue();
		}
	}

	/**
	 * Define o HMAC secret compartilhado entre frontend e backend.
	 *
	 * Útil para atualizar o secret após reinjeção de sessão (ex: após refresh F5).
	 *
	 * @param secret Chave HMAC em Base64 (geralmente de window.sessionCrypto.hmacKeyB64)
	 */
	public setHmacSecret(secret: string | null | undefined): void {
		const normalized = secret == null ? null : String(secret);
		this.session.setHmacSecret(normalized);

		if (normalized) {
			sendToTerminal(
				"✅ [HMAC] Secret atualizado",
				{
					hasHmacSecret: true,
					source: "manual-update",
				},
				LogCategory.CONNECTION,
			);

			// ✅ NOVO: Tentar flush da fila quando HMAC é definido
			// (precisa de token também, mas flushPendingQueue verifica isso)
			this.flushPendingQueue();
		}
	}

	/**
	 * Flush da fila de mensagens pendentes
	 *
	 * ✅ ATUALIZADO (2025): Agora processa as mensagens com autenticação HMAC
	 * ao invés de enviar diretamente sem validação.
	 */
	private flushPendingQueue(): void {
		const token = this.getEffectiveToken();
		const hasHmacSecret = this.authManager.isHmacSecretAvailable();

		// Precisamos de ambos para processar
		if (!token || !hasHmacSecret) {
			sendToTerminal(
				"⏳ [QUEUE] Flush adiado - aguardando credenciais",
				{
					hasToken: !!token,
					hasHmacSecret,
				},
				LogCategory.QUEUE,
			);
			return;
		}

		// Obter itens da fila para reprocessamento
		const items = this.messageQueue.dequeueForReprocessing();
		if (items.length === 0) return;

		sendToTerminal(
			"🔄 [QUEUE] Processando fila pendente com HMAC",
			{
				itemCount: items.length,
			},
			LogCategory.QUEUE,
		);

		// Reprocessar cada item com autenticação HMAC completa
		for (const item of items) {
			try {
				// Usar _sendMessageInternal que faz a autenticação HMAC
				const requestId = this._sendMessageInternal(
					item.action,
					item.data,
					item.callback,
				);

				if (requestId) {
					sendToTerminal(
						"✅ [QUEUE] Mensagem reprocessada",
						{
							action: item.action,
							originalRequestId: item.requestId,
							newRequestId: requestId,
						},
						LogCategory.QUEUE,
					);
				} else {
					// Falha ao enviar - notificar callback de erro
					if (item.callback) {
						try {
							item.callback({
								requestId: item.requestId,
								status: "erro",
								message: "Falha ao reprocessar mensagem da fila",
							} as any);
						} catch (error) {
							// Ignorar erros de callback
						}
					}
				}
			} catch (error) {
				sendToTerminal(
					"❌ [QUEUE] Erro ao reprocessar mensagem",
					{
						action: item.action,
						requestId: item.requestId,
						error: error instanceof Error ? error.message : String(error),
					},
					LogCategory.ERROR,
				);

				// Notificar callback de erro
				if (item.callback) {
					try {
						item.callback({
							requestId: item.requestId,
							status: "erro",
							message:
								error instanceof Error
									? error.message
									: "Erro ao reprocessar mensagem",
						} as any);
					} catch (cbError) {
						// Ignorar erros de callback
					}
				}
			}
		}
	}

	/**
	 * Obtém o token efetivo (de window.sessionToken ou session manager)
	 */
	private getEffectiveToken(): string | null {
		// Preferir window.sessionToken (injetado pelo C#)
		const windowToken = (window as any).sessionToken;
		if (windowToken) return String(windowToken);

		// Fallback para session manager
		return this.session.getSessionToken();
	}

	/**
	 * Monta uma mensagem completa
	 *
	 * ⚠️ CRÍTICO: sessionToken DEVE estar na RAIZ da mensagem, não dentro de data
	 * Conforme PADRAO_VALIDACAO_HMAC.md linha 38-43
	 */
	private buildMessage(
		action: string,
		data: Record<string, any>,
		requestId: string,
	): WebViewMessage {
		const message: WebViewMessage = {
			action,
			requestId,
			data: { ...data },
		};

		// ✅ Checklist item 4: Adicionar sessionToken NA RAIZ (se não for isenta)
		const isTokenExempt = this.authManager.isTokenExemptAction(action);
		if (!isTokenExempt) {
			const effectiveToken = this.getEffectiveToken();
			if (effectiveToken) {
				if (this.isValidSessionToken(effectiveToken)) {
					// ⚠️ IMPORTANTE: sessionToken vai na RAIZ, não dentro de data!
					(message as any).sessionToken = effectiveToken;
				} else {
					const guidRegex =
						/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
					sendToTerminal(
						"🚫 [GUARD] sessionToken inválido - não enviado",
						{
							action,
							tokenLength: effectiveToken.length,
							tokenFormat: guidRegex.test(effectiveToken)
								? "GUID"
								: /^[a-fA-F0-9]+$/.test(effectiveToken)
									? "Hex parcial"
									: "desconhecido",
							expectedFormats: "GUID (36 chars) ou Hex (64 chars)",
						},
						LogCategory.AUTH,
					);
				}
			} else {
				sendToTerminal(
					"🚫 [GUARD] sessionToken ausente - não enviado",
					{
						action,
					},
					LogCategory.AUTH,
				);
			}
		}

		return message;
	}

	/**
	 * Envia uma mensagem para o WebView2/Corel (método privado)
	 *
	 * ⚠️ USO INTERNO: Use sendActionGuarded() para enviar mensagens
	 *
	 * @param action Nome da ação
	 * @param data Dados extras (opcional)
	 * @param callback Callback opcional
	 * @returns ID da requisição se enviada com sucesso, null caso contrário
	 */
	private _sendMessageInternal(
		action: string,
		data: Record<string, any> = {},
		callback?: (response: WebViewResponse) => void,
	): string | null {
		// 1. Mock em desenvolvimento?
		if (this.devMock.shouldMock()) {
			return this.devMock.simulateResponse(action, callback);
		}

		// 2. WebView disponível?
		if (!this.messageValidator.isWebViewAvailable()) {
			// WebView não disponível - silencioso
			return null;
		}

		// 3. Gerar requestId
		const requestId = this.messageSender.generateRequestId();

		// 4. Mapear action para o tracker
		this.tracker.mapAction(requestId, action);

		// 5. Garantir listener configurado
		if (!this.isListenerConfigured) {
			try {
				this.setupListenerInternal();
			} catch (error) {
				// Ignorar erros
			}
		}

		// 6. Verificar se precisa de token
		const isTokenExempt = this.authManager.isTokenExemptAction(action);
		const effectiveToken = this.getEffectiveToken();

		// Log do token (se não for isenta)
		if (!isTokenExempt) {
			this.logManager.logSessionToken(
				action,
				!!effectiveToken,
				effectiveToken ? effectiveToken.substring(0, 20) + "..." : undefined,
			);
		}

		// 6b. Se tiver token mas for inválido (não GUID 36 chars ou hex 64 chars), bloquear envio
		if (
			!isTokenExempt &&
			effectiveToken &&
			!this.isValidSessionToken(effectiveToken)
		) {
			const guidRegex =
				/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
			const isGuid = guidRegex.test(effectiveToken);
			const isHex64 =
				effectiveToken.length === 64 && /^[a-fA-F0-9]+$/.test(effectiveToken);

			sendToTerminal(
				"🚫 [GUARD] Bloqueado - sessionToken inválido",
				{
					action,
					requestId,
					tokenLength: effectiveToken.length,
					tokenPreview: effectiveToken.substring(0, 20),
					isGuid,
					isHex64,
					expectedFormats: "GUID (36 chars) ou Hex (64 chars)",
					hasWindowToken: !!(window as any)?.sessionToken,
					windowTokenLength: (window as any)?.sessionToken?.length ?? 0,
					hasSessionManagerToken: !!this.session.getSessionToken(),
					sessionManagerTokenLength:
						this.session.getSessionToken()?.length ?? 0,
				},
				LogCategory.AUTH,
			);
			return null;
		}

		// 7. Se não tiver token e não for isenta, abortar (não enfileirar)
		if (!isTokenExempt && !effectiveToken) {
			sendToTerminal(
				"🚫 [GUARD] Bloqueado - sem token (não enfileirado)",
				{
					action,
					requestId,
					hasWindowToken: !!(window as any)?.sessionToken,
					windowTokenLength: (window as any)?.sessionToken?.length ?? 0,
					windowTokenType: typeof (window as any)?.sessionToken,
					hasSessionManagerToken: !!this.session.getSessionToken(),
					sessionManagerTokenLength:
						this.session.getSessionToken()?.length ?? 0,
					isTokenExempt,
					willPoll: false,
					recommendation:
						"Verifique se o C# injetou window.sessionToken no DOM",
				},
				LogCategory.AUTH,
			);
			return null;
		}

		// 8. Adicionar autenticação HMAC
		const authResult = this.authManager.addAuthToPayload(
			action,
			data,
			requestId,
		);
		if (!authResult.success) {
			// 🔍 LOG HMAC: Registrar erro de autenticação
			hmacLogger.logError(
				action,
				requestId,
				"HMAC authentication failed",
				authResult.error,
				data,
			);

			// Notificar callback de erro
			if (callback) {
				try {
					callback(authResult.error as any);
				} catch (error) {
					// Ignorar erros de callback
				}
			}
			return requestId; // Retornar requestId mesmo com erro (para rastreamento)
		}

		// 9. Registrar callback se fornecido
		if (callback) {
			// Prioridade: 1. data.timeoutMs (explicito) > 2. API (timeoutService) > 3. ACTION_TIMEOUTS > 4. DEFAULT
			const timeoutMs =
				typeof (data as any)?.timeoutMs === "number"
					? (data as any).timeoutMs
					: timeoutService.getTimeout(action);

			this.tracker.registerHandlerWithTimeout(requestId, callback, timeoutMs);

			if (!this.isListenerConfigured) {
				this.setupListenerInternal();
			}
		}

		// 10. Montar mensagem completa
		const message = this.buildMessage(action, authResult.data!, requestId);

		// 🔍 LOG HMAC: Registrar request completo
		hmacLogger.logRequest(action, requestId, message);

		// 🔍 DEBUG: Log específico para vbcompiler com disablePerformanceMonitor
		if (action === "vbcompiler" && authResult.data) {
			sendToTerminal("🔍 [VBCOMPILER] Payload final antes de serializar", {
				action,
				requestId,
				dataKeys: Object.keys(authResult.data),
				hasDisablePerformanceMonitor:
					"disablePerformanceMonitor" in authResult.data,
				disablePerformanceMonitorValue:
					authResult.data.disablePerformanceMonitor,
				dataPreview: {
					...authResult.data,
					code: authResult.data.code
						? `${String(authResult.data.code).substring(0, 50)}...`
						: "empty",
				},
			});
		}

		// 11. ✅ Log de validação final do checklist conforme PADRAO_VALIDACAO_HMAC.md
		const jsonPayload = JSON.stringify(message);
		this.logManager.logPayload(message, jsonPayload.length);

		// ✅ Validação dos 4 pontos críticos
		if (this.logManager.isDevelopment()) {
			// Log de validação HMAC removido - usar LogManager se necessário
		}

		// Log específico para open-tools-docker
		if (action === "open-tools-docker") {
			this.logManager.logOpenToolsDocker(message);
		}

		// Log de envio (em dev)
		if (this.logManager.isDevelopment()) {
			this.logManager.logSend(action, requestId, message);
		}

		// 12. Enviar mensagem
		return this.messageSender.send(message, callback);
	}

	/**
	 * Envia mensagem com chave de idempotência
	 */
	public sendMessageWithIdempotency(
		action: string,
		data: Record<string, unknown> = {},
		idempotencyKey?: string,
		callback?: (response: WebViewResponse) => void,
	): string | null {
		// 1. Mock em desenvolvimento?
		if (this.devMock.shouldMock()) {
			return this.devMock.simulateResponse(action, callback);
		}

		// 2. WebView disponível?
		if (!this.messageValidator.isWebViewAvailable()) {
			// WebView não disponível - silencioso
			return null;
		}

		// 3. Gerar requestId
		const requestId = this.messageSender.generateRequestId();
		this.tracker.mapAction(requestId, action);

		if (!this.isListenerConfigured) {
			try {
				this.setupListenerInternal();
			} catch (error) {
				// Ignorar
			}
		}

		// 4. Verificar token
		const isTokenExempt = this.authManager.isTokenExemptAction(action);
		const effectiveToken = this.getEffectiveToken();

		if (!isTokenExempt && !effectiveToken) {
			if (callback) {
				this.tracker.registerHandlerWithTimeout(
					requestId,
					callback,
					DEFAULT_REQUEST_TIMEOUT_MS,
				);
			}

			this.messageQueue.enqueue({
				action,
				data: data as Record<string, any>,
				requestId,
				callback,
			});

			const maybeToken = (window as any)?.sessionToken;
			if (maybeToken) {
				this.setSessionToken(maybeToken);
			}

			return requestId;
		}

		// 5. Adicionar HMAC
		const authResult = this.authManager.addAuthToPayload(
			action,
			data as Record<string, any>,
			requestId,
		);
		if (!authResult.success) {
			if (callback) {
				try {
					callback(authResult.error as any);
				} catch (error) {
					// Ignorar
				}
			}
			return requestId;
		}

		// 6. Registrar callback
		if (callback) {
			// Prioridade: 1. data.timeoutMs (explicito) > 2. API (timeoutService) > 3. ACTION_TIMEOUTS > 4. DEFAULT
			const dt = data as { timeoutMs?: unknown };
			const timeoutMs =
				typeof dt.timeoutMs === "number"
					? (dt.timeoutMs as number)
					: timeoutService.getTimeout(action);

			this.tracker.registerHandlerWithTimeout(requestId, callback, timeoutMs);

			if (!this.isListenerConfigured) {
				this.setupListenerInternal();
			}
		}

		// 7. Montar mensagem e enviar
		const message = this.buildMessage(action, authResult.data!, requestId);

		// Log
		if (this.logManager.isDevelopment()) {
			this.logManager.logSend(action, requestId, message);
		}

		// Enviar com idempotência
		if (idempotencyKey) {
			return this.messageSender.sendWithIdempotency(
				message,
				idempotencyKey,
				callback,
			);
		} else {
			return this.messageSender.send(message, callback);
		}
	}

	/**
	 * Envia action com validação de sessão/autorização
	 *
	 * ✅ ATUALIZADO (2025): Ao invés de bloquear silenciosamente quando token/HMAC
	 * não estão disponíveis, agora enfileira a mensagem e tenta obter as credenciais.
	 *
	 * Comportamento:
	 * 1. Se WebView não disponível: enfileira e aguarda com polling
	 * 2. Se token/HMAC estão disponíveis: envia imediatamente
	 * 3. Se faltam credenciais: enfileira e tenta session:hello automaticamente
	 * 4. A fila tem timeout de 30s - se não conseguir, callback recebe erro
	 */
	public sendActionGuarded(
		action: string,
		data: Record<string, any> = {},
		callback?: (response: WebViewResponse) => void,
		customTimeoutMs?: number,
	): string | null {
		// ✅ CORREÇÃO: Verificar WebView PRIMEIRO
		// Se não disponível, enfileirar e fazer polling (ao invés de retornar null)
		if (!this.messageValidator.isWebViewAvailable()) {
			const requestId = this.messageSender.generateRequestId();
			this.tracker.mapAction(requestId, action);

			sendToTerminal(
				"📥 [GUARD] WebView não disponível - enfileirando mensagem",
				{
					action,
					requestId,
					willPollWebView: true,
				},
				LogCategory.AUTH,
			);

			// Enfileirar mensagem
			this.messageQueue.enqueue({
				action,
				data,
				requestId,
				callback,
			});

			// Iniciar polling para WebView + credenciais
			this.startWebViewPolling();

			return requestId;
		}

		const isTokenExempt = this.authManager.isTokenExemptAction(action);
		const isHmacExempt = this.authManager.isHmacExemptAction(action);
		const token = this.getEffectiveToken();
		const hasHmacSecret = this.authManager.isHmacSecretAvailable();

		// ✅ Se tudo disponível, enviar imediatamente
		const needsToken = !isTokenExempt;
		const needsHmac = !isHmacExempt;
		const canSendNow =
			(!needsToken || !!token) && (!needsHmac || hasHmacSecret);

		if (canSendNow) {
			// Usar timeout customizado se fornecido
			if (customTimeoutMs && callback) {
				const requestId = this._sendMessageInternal(action, data, callback);
				if (requestId) {
					this.tracker.clearTimeoutFor(requestId);
					this.tracker.registerHandlerWithTimeout(
						requestId,
						callback,
						customTimeoutMs,
					);
				}
				return requestId;
			}

			// Envio normal
			return this._sendMessageInternal(action, data, callback);
		}

		// ✅ NOVO: Enfileirar ao invés de bloquear
		// Gerar requestId para rastreamento
		const requestId = this.messageSender.generateRequestId();

		// Mapear action para o tracker (para timeout handling)
		this.tracker.mapAction(requestId, action);

		const missingToken = needsToken && !token;
		const missingHmac = needsHmac && !hasHmacSecret;

		sendToTerminal(
			"📥 [GUARD] Credenciais ausentes - enfileirando mensagem",
			{
				action,
				requestId,
				missingToken,
				missingHmac,
				willRetry: true,
			},
			LogCategory.AUTH,
		);

		// Enfileirar mensagem
		this.messageQueue.enqueue({
			action,
			data,
			requestId,
			callback,
		});

		// ✅ NOVO: Tentar obter credenciais automaticamente
		// Enviar session:hello para solicitar token do C#
		// Isso é fire-and-forget, não bloqueante
		this.requestCredentialsIfNeeded(missingToken, missingHmac);

		// Retornar requestId para rastreamento (mesmo que enfileirado)
		return requestId;
	}

	private dispatchActionWithCurrentCredentials(
		action: string,
		data: Record<string, any> = {},
		callback?: (response: WebViewResponse) => void,
		customTimeoutMs?: number,
	): string | null {
		const isTokenExempt = this.authManager.isTokenExemptAction(action);
		const isHmacExempt = this.authManager.isHmacExemptAction(action);
		const token = this.getEffectiveToken();
		const hasHmacSecret = this.authManager.isHmacSecretAvailable();

		// ✅ Se tudo disponível, enviar imediatamente
		const needsToken = !isTokenExempt;
		const needsHmac = !isHmacExempt;
		const canSendNow =
			(!needsToken || !!token) && (!needsHmac || hasHmacSecret);

		if (canSendNow) {
			// ✅ NOVO: Wrapper de callback para detectar erro de launcher e fazer retry
			const wrappedCallback = this.createLauncherAwareCallback(
				action,
				data,
				callback,
				customTimeoutMs,
			);

			// Usar timeout customizado se fornecido
			if (customTimeoutMs && wrappedCallback) {
				const requestId = this._sendMessageInternal(
					action,
					data,
					wrappedCallback,
				);
				if (requestId) {
					this.tracker.clearTimeoutFor(requestId);
					this.tracker.registerHandlerWithTimeout(
						requestId,
						wrappedCallback,
						customTimeoutMs,
					);
				}
				return requestId;
			}

			// Envio normal com wrapper
			return this._sendMessageInternal(action, data, wrappedCallback);
		}

		// ✅ NOVO: Enfileirar ao invés de bloquear
		// Gerar requestId para rastreamento
		const requestId = this.messageSender.generateRequestId();

		// Mapear action para o tracker (para timeout handling)
		this.tracker.mapAction(requestId, action);

		const missingToken = needsToken && !token;
		const missingHmac = needsHmac && !hasHmacSecret;

		sendToTerminal(
			"📥 [GUARD] Credenciais ausentes - enfileirando mensagem",
			{
				action,
				requestId,
				missingToken,
				missingHmac,
				isTokenExempt,
				isHmacExempt,
				hasToken: !!token,
				hasHmacSecret,
				willRetry: true,
				queueType: missingHmac
					? "HMAC_MISSING"
					: missingToken
						? "TOKEN_MISSING"
						: "UNKNOWN",
			},
			LogCategory.AUTH,
		);

		// Enfileirar mensagem
		this.messageQueue.enqueue({
			action,
			data,
			requestId,
			callback,
		});

		// ✅ NOVO: Tentar obter credenciais automaticamente
		// Enviar session:hello para solicitar token do C#
		// Isso é fire-and-forget, não bloqueante
		this.requestCredentialsIfNeeded(missingToken, missingHmac);

		// Retornar requestId para rastreamento (mesmo que enfileirado)
		return requestId;
	}

	/**
	 * ✅ NOVO: Cria callback com detecção de erro de launcher
	 *
	 * Quando o launcher retorna 'launcher_session_required', a ação é
	 * enfileirada automaticamente para retry quando o sistema ficar pronto.
	 */
	private createLauncherAwareCallback(
		action: string,
		data: Record<string, any>,
		originalCallback?: (response: WebViewResponse) => void,
		customTimeoutMs?: number,
	): ((response: WebViewResponse) => void) | undefined {
		if (!originalCallback) return undefined;

		return (response: WebViewResponse) => {
			const anyResponse = response as any;
			const message = anyResponse?.message || anyResponse?.error || "";
			const code = anyResponse?.code || "";
			const result = anyResponse?.result || {};

			// Detectar se é erro de launcher não pronto
			const isLauncherNotReady =
				message.includes("launcher_session_required") ||
				code === "launcher_session_required" ||
				result?.launcherState === "booting";

			if (isLauncherNotReady) {
				// Atualizar estado do launcher
				launcherReadiness.updateStateFromResponse(anyResponse);

				// Enfileirar ação para retry automático
				const queuedId = launcherReadiness.enqueueAction(
					action,
					data,
					originalCallback,
					customTimeoutMs,
				);

				sendToTerminal(
					"🔄 [LAUNCHER_RETRY] Ação enfileirada para retry automático",
					{
						action,
						queuedId,
						launcherState: result?.launcherState || "booting",
						message: message,
					},
					LogCategory.QUEUE,
				);

				// Não chamar o callback ainda - aguardar retry
				return;
			}

			// Se é resposta de sucesso, atualizar estado para operational
			if (anyResponse?.status === "ok") {
				launcherReadiness.updateStateFromResponse(anyResponse);
			}

			// Chamar callback original
			originalCallback(response);
		};
	}

	/**
	 * Solicita credenciais do C# se necessário
	 *
	 * ✅ ATUALIZADO (2025): O C# injeta as credenciais diretamente no DOM via
	 * AddScriptToExecuteOnDocumentCreatedAsync, não via mensagem.
	 * Por isso, fazemos polling do DOM para detectar quando aparecem.
	 *
	 * @param needsToken Se precisa de token
	 * @param needsHmac Se precisa de HMAC
	 */
	private requestCredentialsIfNeeded(
		needsToken: boolean,
		needsHmac: boolean,
	): void {
		// Evitar múltiplas solicitações simultâneas
		const now = Date.now();
		const lastRequest = (this as any)._lastCredentialRequest || 0;
		const MIN_INTERVAL_MS = 1000; // Não solicitar mais de uma vez por segundo

		if (now - lastRequest < MIN_INTERVAL_MS) {
			return;
		}

		(this as any)._lastCredentialRequest = now;

		// Verificar se WebView está disponível
		if (!this.messageValidator.isWebViewAvailable()) {
			sendToTerminal(
				"⚠️ [GUARD] WebView não disponível - não é possível solicitar credenciais",
				{},
				LogCategory.AUTH,
			);
			return;
		}

		sendToTerminal(
			"🔄 [GUARD] Aguardando credenciais do C# (polling DOM)",
			{
				needsToken,
				needsHmac,
			},
			LogCategory.AUTH,
		);

		// ✅ NOVO: Polling do DOM para detectar quando C# injeta as credenciais
		// O C# injeta via AddScriptToExecuteOnDocumentCreatedAsync, não via mensagem
		this.startCredentialPolling();

		// Também enviar session:hello como fallback (pode ajudar a obter sessionToken)
		try {
			const helloData = {
				timestamp: Date.now(),
				dockerId: (window as any)?.dockerId,
			};

			this._sendMessageInternal("session:hello", helloData, (response) => {
				if (response?.status === "ok") {
					sendToTerminal(
						"✅ [GUARD] Resposta session:hello recebida",
						{},
						LogCategory.AUTH,
					);
				}
			});
		} catch (error) {
			sendToTerminal(
				"❌ [GUARD] Erro ao enviar session:hello",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				LogCategory.ERROR,
			);
		}
	}

	/**
	 * Inicia polling do DOM para detectar quando o C# injeta as credenciais.
	 *
	 * O C# usa AddScriptToExecuteOnDocumentCreatedAsync para injetar:
	 * - window.sessionToken
	 * - window.sessionCrypto.hmacKeyB64
	 * - window.dockerId
	 *
	 * Este polling verifica periodicamente se as variáveis aparecem no DOM.
	 */
	private startCredentialPolling(): void {
		// Evitar múltiplos pollings simultâneos
		if ((this as any)._credentialPollingActive) {
			return;
		}

		(this as any)._credentialPollingActive = true;

		const POLL_INTERVAL_MS = 100; // Verificar a cada 100ms
		const MAX_POLL_TIME_MS = 10000; // Máximo 10 segundos
		const startTime = Date.now();
		let pollCount = 0;

		const pollInterval = setInterval(() => {
			pollCount++;
			const elapsed = Date.now() - startTime;

			// Verificar se as credenciais aparecem no DOM
			const windowToken = (window as any)?.sessionToken;
			const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;
			const windowDockerId = (window as any)?.dockerId;
			const hasToken = !!windowToken && this.isValidSessionToken(windowToken);
			const hasHmac = !!windowHmac;
			const tokenValid = windowToken
				? this.isValidSessionToken(windowToken)
				: false;
			const tokenLength = windowToken?.length ?? 0;
			const hmacLength = windowHmac?.length ?? 0;

			if (hasToken && hasHmac) {
				// 🎯 Credenciais encontradas no DOM!
				clearInterval(pollInterval);
				(this as any)._credentialPollingActive = false;

				sendToTerminal(
					"✅ [GUARD:POLL] Credenciais detectadas no DOM",
					{
						pollCount,
						elapsedMs: elapsed,
						tokenLength,
						hmacLength,
						tokenValid,
						dockerId: windowDockerId || "ausente",
						tokenPreview: windowToken
							? `${windowToken.substring(0, 8)}...`
							: "ausente",
						hmacPreview: windowHmac
							? `${windowHmac.substring(0, 8)}...`
							: "ausente",
					},
					LogCategory.AUTH,
				);

				// Atualizar session manager com as credenciais do DOM
				try {
					this.setSessionToken(windowToken);
					this.setHmacSecret(windowHmac);
					sendToTerminal(
						"✅ [GUARD:POLL] Credenciais configuradas com sucesso",
						{
							queueSize: this.messageQueue.size(),
						},
						LogCategory.AUTH,
					);
				} catch (err) {
					sendToTerminal(
						"❌ [GUARD:POLL] Erro ao configurar credenciais",
						{
							error: err instanceof Error ? err.message : String(err),
							errorStack: err instanceof Error ? err.stack : undefined,
						},
						LogCategory.ERROR,
					);
				}

				// flushPendingQueue será chamado automaticamente por setSessionToken/setHmacSecret
				return;
			}

			// Timeout do polling
			if (elapsed >= MAX_POLL_TIME_MS) {
				clearInterval(pollInterval);
				(this as any)._credentialPollingActive = false;

				const missingItems: string[] = [];
				if (!windowToken) missingItems.push("sessionToken ausente");
				else if (!tokenValid)
					missingItems.push(
						`sessionToken inválido (length: ${tokenLength}, esperado: 64 hex)`,
					);
				if (!windowHmac) missingItems.push("hmacKeyB64 ausente");

				sendToTerminal(
					"⏱️ [GUARD:POLL] Timeout aguardando credenciais no DOM",
					{
						pollCount,
						elapsedMs: elapsed,
						hasToken,
						hasHmac,
						tokenValid,
						tokenLength,
						hmacLength,
						dockerId: windowDockerId || "ausente",
						missingItems,
						tokenPreview: windowToken
							? `${windowToken.substring(0, 20)}...`
							: "ausente",
						hmacPreview: windowHmac
							? `${windowHmac.substring(0, 20)}...`
							: "ausente",
						queueSize: this.messageQueue.size(),
					},
					LogCategory.ERROR,
				);

				// Se tem token mas não HMAC, pelo menos atualizar o token
				if (hasToken && windowToken) {
					try {
						this.setSessionToken(windowToken);
						sendToTerminal(
							"⚠️ [GUARD:POLL] Token configurado, mas HMAC ainda ausente",
							{},
							LogCategory.AUTH,
						);
					} catch (err) {
						sendToTerminal(
							"❌ [GUARD:POLL] Erro ao configurar token após timeout",
							{
								error: err instanceof Error ? err.message : String(err),
							},
							LogCategory.ERROR,
						);
					}
				}

				return;
			}

			// Log intermediário a cada 2 segundos
			if (pollCount % 20 === 0) {
				const missingItems: string[] = [];
				if (!windowToken) missingItems.push("sessionToken");
				else if (!tokenValid) missingItems.push("sessionToken válido");
				if (!windowHmac) missingItems.push("hmacKeyB64");

				sendToTerminal(
					"🔄 [GUARD:POLL] Aguardando credenciais...",
					{
						pollCount,
						elapsedMs: elapsed,
						hasToken,
						hasHmac,
						tokenValid,
						tokenLength,
						hmacLength,
						missingItems,
						dockerId: windowDockerId || "ausente",
					},
					LogCategory.AUTH,
				);
			}
		}, POLL_INTERVAL_MS);
	}

	/**
	 * Inicia polling do WebView para detectar quando fica disponível.
	 *
	 * Isso é necessário porque o WebView2 pode não estar pronto
	 * imediatamente quando o React monta os componentes.
	 */
	private startWebViewPolling(): void {
		// Evitar múltiplos pollings simultâneos
		if ((this as any)._webViewPollingActive) {
			return;
		}

		(this as any)._webViewPollingActive = true;

		const POLL_INTERVAL_MS = 50; // Verificar a cada 50ms (mais rápido)
		const MAX_POLL_TIME_MS = 15000; // Máximo 15 segundos
		const startTime = Date.now();
		let pollCount = 0;

		const pollInterval = setInterval(() => {
			pollCount++;
			const elapsed = Date.now() - startTime;

			const hasWebView = this.messageValidator.isWebViewAvailable();

			if (hasWebView) {
				clearInterval(pollInterval);
				(this as any)._webViewPollingActive = false;

				// Verificar estado das credenciais quando WebView aparece
				const windowToken = (window as any)?.sessionToken;
				const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;
				const hasToken = !!windowToken && this.isValidSessionToken(windowToken);
				const hasHmac = !!windowHmac;

				sendToTerminal(
					"✅ [GUARD:POLL] WebView detectado",
					{
						pollCount,
						elapsedMs: elapsed,
						hasToken,
						hasHmac,
						tokenLength: windowToken?.length ?? 0,
						hmacLength: windowHmac?.length ?? 0,
						queueSize: this.messageQueue.size(),
					},
					LogCategory.AUTH,
				);

				// Agora verificar credenciais e fazer flush
				this.startCredentialPolling();
				return;
			}

			if (elapsed >= MAX_POLL_TIME_MS) {
				clearInterval(pollInterval);
				(this as any)._webViewPollingActive = false;

				// Diagnóstico completo do estado
				const windowToken = (window as any)?.sessionToken;
				const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;
				const diagnostics = this.getDiagnostics();

				sendToTerminal(
					"⏱️ [GUARD:POLL] Timeout aguardando WebView",
					{
						pollCount,
						elapsedMs: elapsed,
						hasToken: !!windowToken,
						hasHmac: !!windowHmac,
						tokenLength: windowToken?.length ?? 0,
						hmacLength: windowHmac?.length ?? 0,
						queueSize: this.messageQueue.size(),
						recommendations: diagnostics.recommendations,
						chromeWebView: !!(window as any).chrome?.webview,
						userAgent: navigator.userAgent,
					},
					LogCategory.ERROR,
				);

				// Notificar callbacks de erro
				const items = this.messageQueue.dequeueForReprocessing();
				sendToTerminal(
					"📤 [GUARD:POLL] Notificando callbacks de erro",
					{
						itemCount: items.length,
					},
					LogCategory.ERROR,
				);

				for (const item of items) {
					if (item.callback) {
						try {
							item.callback({
								requestId: item.requestId,
								status: "error",
								message: "WebView não disponível após timeout (15s)",
								errorCode: "WEBVIEW_TIMEOUT",
								errorDetails: {
									elapsedMs: elapsed,
									pollCount,
									hasToken: !!windowToken,
									hasHmac: !!windowHmac,
									recommendations: diagnostics.recommendations,
								},
							} as any);
						} catch (e) {
							sendToTerminal(
								"❌ [GUARD:POLL] Erro ao notificar callback",
								{
									requestId: item.requestId,
									error: e instanceof Error ? e.message : String(e),
								},
								LogCategory.ERROR,
							);
						}
					}
				}
				return;
			}

			if (pollCount % 40 === 0) {
				const windowToken = (window as any)?.sessionToken;
				const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;
				sendToTerminal(
					"🔄 [GUARD:POLL] Aguardando WebView...",
					{
						pollCount,
						elapsedMs: elapsed,
						hasToken: !!windowToken,
						hasHmac: !!windowHmac,
						queueSize: this.messageQueue.size(),
						chromeWebView: !!(window as any).chrome?.webview,
					},
					LogCategory.AUTH,
				);
			}
		}, POLL_INTERVAL_MS);
	}

	/**
	 * Configura listener interno para mensagens do WebView2
	 */
	private setupListenerInternal(): void {
		if (this.isListenerConfigured) return;

		const hasWebView = !!(window as any)?.chrome?.webview;
		if (!hasWebView) {
			sendToTerminal(
				"⚠️ [CONNECTION] Listener do MessageRouter adiado - WebView2 ainda não disponível",
				{
					dockerId: (window as any)?.dockerId || "not-provided",
					bootstrapInitialized: this.bootstrapInitialized,
				},
				LogCategory.CONNECTION,
			);
			return;
		}

		this.router.setupListenerInternal((response, raw) => {
			try {
				// Capturar token de sessão rotacionado (Token Rotation)
				// ⚠️ CRÍTICO: Backend sempre retorna novo token em Release
				// Token sempre vem na RAIZ da resposta, nunca em data.sessionToken
				try {
					const rawObj =
						typeof raw === "string" ? JSON.parse(raw as string) : raw;
					if (rawObj && typeof rawObj === "object") {
						// Token sempre na raiz conforme documentação (RESPOSTAS_DUVIDAS.md)
						const tokenCandidate = (rawObj as any)?.sessionToken;

						// Atualizar token se presente (todas as respostas podem rotacionar token)
						if (tokenCandidate && typeof tokenCandidate === "string") {
							const action =
								(rawObj as any)?.action || (response as any)?.requestId
									? this.tracker.getAction(
											(response as any).requestId as string,
										)
									: undefined;

							const isValid = this.isValidSessionToken(tokenCandidate);
							const currentToken = this.session.getSessionToken();
							const isNewToken = tokenCandidate !== currentToken;

							if (!isValid) {
								// Só loga token inválido (raro, merece atenção)
								const validation =
									TokenValidator.validateSessionToken(tokenCandidate);
								sendToTerminal(
									"🚫 [TOKEN_ROTATION] Token inválido ignorado",
									{
										action: action || "unknown",
										tokenLength: tokenCandidate.length,
										reason: validation.reason,
									},
									LogCategory.AUTH,
								);
							} else if (isNewToken) {
								// Atualiza o token (sempre)
								this.setSessionToken(tokenCandidate);

								// Log com throttle para evitar spam (apenas 1 log a cada 3s)
								const now = Date.now();
								if (
									now - this.lastTokenRotationLogAt >
									CorelCommunication.TOKEN_ROTATION_LOG_THROTTLE_MS
								) {
									const isHandshake = action === "session:hello" || !action;
									sendToTerminal(
										"🔄 [TOKEN_ROTATION] Token atualizado",
										{
											action: action || "unknown",
											type: isHandshake ? "handshake" : "rotation",
										},
										LogCategory.CONNECTION,
									);
									this.lastTokenRotationLogAt = now;
								}
							}
							// Se token é válido mas igual ao atual, não loga (silencioso)
						}
					}
				} catch (err) {
					sendToTerminal(
						"❌ [TOKEN_ROTATION] Erro ao processar token",
						{
							error: err instanceof Error ? err.message : String(err),
							rawType: typeof raw,
						},
						LogCategory.ERROR,
					);
				}

				// Log de recebimento
				if (this.logManager.isDevelopment()) {
					const originalAction = (response as any)?.requestId
						? this.tracker.getAction((response as any).requestId as string)
						: undefined;
					this.logManager.logReceive(response, originalAction);
				}

				// 🔍 LOG HMAC: Registrar response
				const responseRequestId = (response as any)?.requestId;
				if (responseRequestId) {
					const action = this.tracker.getAction(responseRequestId);
					if (action) {
						// Verificar se é erro (múltiplos formatos possíveis)
						const status = (response as any)?.status;
						const hasError = (response as any)?.error;
						const isError =
							status === "erro" ||
							status === "error" ||
							status === "timeout" ||
							hasError ||
							(status !== "ok" &&
								status !== "progress_update" &&
								status !== undefined);

						if (isError) {
							const errorMessage =
								(response as any)?.message ||
								(response as any)?.error ||
								(status === "timeout" ? "Timeout" : "Unknown error");

							hmacLogger.logError(
								action,
								responseRequestId,
								errorMessage,
								response,
								undefined,
							);
						} else {
							hmacLogger.logResponse(action, responseRequestId, response);
						}
					}
				}

				// DEBUG: Log para capturar-objetos-individuais (antes de resolver)
				if (responseRequestId) {
					const actionForDebug = this.tracker.getAction(responseRequestId);
					if (actionForDebug === "capturar-objetos-individuais") {
						sendToTerminal(
							"🔍 [COMM] Mensagem recebida do WebView2 para capturar-objetos-individuais",
							{
								requestId: responseRequestId,
								status: (response as any)?.status,
								hasResult: !!response?.result,
								hasObjeto: !!response?.result?.objeto,
								responseType: typeof response,
								responseKeys: Object.keys(response || {}),
							},
							LogCategory.CONNECTION,
						);
					}
				}

				// Resolver via tracker
				this.tracker.resolveResponse(response);
				this.lastResponseAt = Date.now();

				// Callback padrão
				if (this.defaultCallback) {
					try {
						this.defaultCallback(response);
					} catch (err) {
						sendToTerminal(
							"❌ [CALLBACK] Erro no callback padrão",
							{
								error: err instanceof Error ? err.message : String(err),
							},
							LogCategory.ERROR,
						);
					}
				}

				// ✅ Enviar health:pong imediato quando receber token válido (mantém conexão viva)
				const tokenCandidate = (response as any)?.sessionToken;
				if (
					tokenCandidate &&
					typeof tokenCandidate === "string" &&
					this.isValidSessionToken(tokenCandidate)
				) {
					const now = Date.now();
					const isHealthOrStatus =
						(response as any)?.action === "health:pong" ||
						(response as any)?.action === "app-status";
					const canSendHealth =
						!isHealthOrStatus &&
						(!this.lastHealthSentAt || now - this.lastHealthSentAt > 15000);
					if (canSendHealth) {
						corelCommunication.sendActionGuarded("health:pong", {
							timestamp: now,
							apiOnline: (window as any)?.__API_ONLINE ?? true,
						});
						this.lastHealthSentAt = now;
					}
				}
			} catch (error) {
				sendToTerminal(
					"❌ [RESPONSE] Erro ao processar resposta",
					{
						error: error instanceof Error ? error.message : String(error),
					},
					LogCategory.ERROR,
				);
			}
		});

		this.isListenerConfigured = true;
		sendToTerminal(
			"✅ [CONNECTION] CorelCommunication listener configurado",
			{
				dockerId: (window as any)?.dockerId || "not-provided",
			},
			LogCategory.CONNECTION,
		);
	}

	/**
	 * Configura listener global
	 *
	 * ✅ SEGURO: Se já existe um callback, cria wrapper que chama ambos
	 * Isso permite múltiplos listeners sem quebrar funcionalidade existente
	 */
	public setupListener(
		callback: (response: WebViewResponse) => void,
	): () => void {
		// ⚠️ IMPORTANTE: Se já existe um callback, criar wrapper que chama ambos
		const previousCallback = this.defaultCallback;

		const wrapperCallback = (response: WebViewResponse) => {
			// Chamar callback anterior primeiro (preserva funcionalidade existente)
			if (previousCallback) {
				try {
					previousCallback(response);
				} catch (error) {
					sendToTerminal(
						"❌ [CALLBACK] Erro no callback anterior",
						{
							error: error instanceof Error ? error.message : String(error),
						},
						LogCategory.ERROR,
					);
				}
			}

			// Chamar novo callback
			try {
				callback(response);
			} catch (error) {
				sendToTerminal(
					"❌ [CALLBACK] Erro no callback novo",
					{
						error: error instanceof Error ? error.message : String(error),
					},
					LogCategory.ERROR,
				);
			}
		};

		this.defaultCallback = wrapperCallback;

		if (!this.isListenerConfigured) {
			this.setupListenerInternal();
		}

		return () => {
			// Se estamos removendo este listener, restaurar callback anterior
			// Isso funciona mesmo com múltiplos wrappers encadeados
			if (this.defaultCallback === wrapperCallback) {
				this.defaultCallback = previousCallback;
			} else if (
				this.defaultCallback &&
				typeof this.defaultCallback === "function"
			) {
				// Se o callback atual é outro wrapper, tentar remover nosso callback da cadeia
				// (caso complexo - normalmente não acontece se callbacks são removidos na ordem inversa)
				// Por segurança, apenas restaurar o anterior
				this.defaultCallback = previousCallback;
			}
		};
	}

	/**
	 * Remove todos os listeners
	 */
	public removeAllListeners(): void {
		if (window.chrome?.webview) {
			this.tracker.clearAll();
			this.defaultCallback = null;
		}
	}

	/**
	 * Retorna o timestamp da última resposta recebida (ms)
	 */
	public getLastResponseAt(): number | null {
		return this.lastResponseAt;
	}

	/**
	 * Verifica se há operações pendentes aguardando resposta
	 * @returns true se há operações pendentes, false caso contrário
	 */
	public hasPendingRequests(): boolean {
		return this.tracker.hasPendingRequests();
	}

	/**
	 * Retorna o número de operações pendentes
	 * @returns Número de handlers aguardando resposta
	 */
	public getPendingRequestsCount(): number {
		return this.tracker.getPendingCount();
	}

	/**
	 * Retorna informações sobre operações pendentes (para debug)
	 * @returns Array com informações das operações pendentes
	 */
	public getPendingRequestsInfo(): Array<{
		requestId: string;
		action: string | undefined;
	}> {
		return this.tracker.getPendingRequestsInfo();
	}

	/**
	 * Verifica se há operações pendentes para uma action específica
	 * @param action - Nome da action a verificar
	 * @returns true se há operações pendentes para essa action
	 */
	public hasPendingRequestsForAction(action: string): boolean {
		return this.tracker.hasPendingRequestsForAction(action);
	}

	/**
	 * Registra handler para eventos C# → Frontend
	 */
	public registerEventHandler(
		action: string,
		handler: (data: any) => void,
	): void {
		this.eventManager.registerHandler(action, handler);

		if (!this.isListenerConfigured) {
			this.setupListenerInternal();
		}
	}

	/**
	 * Inicializa comunicação aguardando bootstrap do C#
	 */
	public async initializeFromBootstrap(timeout = 5000): Promise<void> {
		try {
			// Log inicial com estado atual
			const hasWebView = !!(window as any)?.chrome?.webview;
			const windowToken = (window as any)?.sessionToken;
			const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;

			sendToTerminal(
				"🔍 [BOOTSTRAP] Iniciando bootstrap",
				{
					timeout,
					hasWebView,
					windowTokenPresent: !!windowToken,
					windowTokenLength: windowToken?.length ?? 0,
					windowHmacPresent: !!windowHmac,
					dockerId: (window as any)?.dockerId,
				},
				LogCategory.CONNECTION,
			);

			const bootstrap: BootstrapData = await bootstrapWithFallback(timeout);

			sendToTerminal(
				"📥 [BOOTSTRAP] Dados recebidos",
				{
					hasSessionToken: !!bootstrap.sessionToken,
					tokenLength: bootstrap.sessionToken?.length ?? 0,
					hasHmacSecret: !!bootstrap.hmacSecret,
					hasFeatures: !!bootstrap.features,
				},
				LogCategory.CONNECTION,
			);

			if (this.isValidSessionToken(bootstrap.sessionToken)) {
				this.setSessionToken(bootstrap.sessionToken);
				sendToTerminal(
					"✅ [BOOTSTRAP] sessionToken válido configurado",
					{
						tokenLength: bootstrap.sessionToken.length,
						tokenPreview: bootstrap.sessionToken.substring(0, 8) + "...",
					},
					LogCategory.CONNECTION,
				);
			} else {
				const tokenStr =
					typeof bootstrap.sessionToken === "string"
						? bootstrap.sessionToken
						: "";
				const guidRegexBoot =
					/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
				const isGuidBoot = guidRegexBoot.test(tokenStr);
				const isHex64Boot =
					tokenStr.length === 64 && /^[a-fA-F0-9]+$/.test(tokenStr);

				sendToTerminal(
					"🚫 [BOOTSTRAP] sessionToken inválido recebido",
					{
						tokenLength: tokenStr.length,
						tokenFormat: isGuidBoot
							? "GUID"
							: isHex64Boot
								? "Hex"
								: "desconhecido",
						expectedFormats: "GUID (36 chars) ou Hex (64 chars)",
						tokenPreview: tokenStr.substring(0, 20) || "undefined",
					},
					LogCategory.AUTH,
				);
				throw new Error(
					`Bootstrap retornou sessionToken inválido (length: ${tokenStr.length}, esperado: GUID 36 chars ou Hex 64 chars)`,
				);
			}

			if (bootstrap.hmacSecret) {
				this.session.setHmacSecret(bootstrap.hmacSecret);
				sendToTerminal(
					"✅ [BOOTSTRAP] HMAC secret configurado",
					{
						hasHmacSecret: true,
						source: "bootstrap",
						hmacLength: bootstrap.hmacSecret.length,
					},
					LogCategory.CONNECTION,
				);
			} else {
				sendToTerminal(
					"⚠️ [BOOTSTRAP] HMAC secret não recebido no bootstrap",
					{
						windowHmacPresent: !!windowHmac,
					},
					LogCategory.CONNECTION,
				);
			}

			if (bootstrap.features) {
				(window as any).__corelFeatures = bootstrap.features;
			}

			this.bootstrapInitialized = true;

			sendToTerminal(
				"✅ [BOOTSTRAP] Comunicação inicializada",
				{
					hasSessionToken: !!bootstrap.sessionToken,
					hasHmacSecret: !!bootstrap.hmacSecret,
					hasFeatures: !!bootstrap.features,
					bootstrapInitialized: this.bootstrapInitialized,
				},
				LogCategory.CONNECTION,
			);
		} catch (error) {
			this.bootstrapInitialized = false;
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			const hasWebView = !!(window as any)?.chrome?.webview;
			const windowToken = (window as any)?.sessionToken;

			sendToTerminal(
				"❌ [BOOTSTRAP] Erro na inicialização",
				{
					error: errorMessage,
					hasWebView,
					windowTokenPresent: !!windowToken,
					windowTokenLength: windowToken?.length ?? 0,
					dockerId: (window as any)?.dockerId,
				},
				LogCategory.ERROR,
			);
			throw error;
		}
	}

	/**
	 * Atualiza sessão a partir de variáveis reinjetadas pelo C# (ex: após refresh F5).
	 *
	 * Este método é chamado quando o evento `oneprint:session-reinjected` é disparado.
	 * Atualiza tanto o sessionToken quanto o HMAC secret se disponíveis.
	 *
	 * @returns true se a sessão foi atualizada com sucesso, false caso contrário
	 */
	public updateSessionFromReinjection(): boolean {
		try {
			const sessionToken = (window as any)?.sessionToken;
			const hmacKeyB64 = (window as any)?.sessionCrypto?.hmacKeyB64;

			if (!sessionToken) {
				sendToTerminal(
					"⚠️ [SESSION_REINJECTED] sessionToken não disponível",
					{},
					LogCategory.CONNECTION,
				);
				return false;
			}

			if (!this.isValidSessionToken(sessionToken)) {
				const tokenStrReinjected = String(sessionToken);
				const guidRegexReinjected =
					/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
				const isGuidReinjected = guidRegexReinjected.test(tokenStrReinjected);
				const isHex64Reinjected =
					tokenStrReinjected.length === 64 &&
					/^[a-fA-F0-9]+$/.test(tokenStrReinjected);

				sendToTerminal(
					"🚫 [SESSION_REINJECTED] sessionToken inválido (ignorado)",
					{
						tokenLength: tokenStrReinjected.length,
						tokenFormat: isGuidReinjected
							? "GUID"
							: isHex64Reinjected
								? "Hex"
								: "desconhecido",
						expectedFormats: "GUID (36 chars) ou Hex (64 chars)",
					},
					LogCategory.AUTH,
				);
				return false;
			}

			// Atualizar token
			this.setSessionToken(sessionToken);

			// Atualizar HMAC secret se disponível
			if (hmacKeyB64) {
				this.setHmacSecret(hmacKeyB64);
			} else {
				sendToTerminal(
					"⚠️ [SESSION_REINJECTED] HMAC key não disponível",
					{},
					LogCategory.CONNECTION,
				);
			}

			// Se bootstrap inicial falhou, tentar reinicializar
			if (!this.bootstrapInitialized && sessionToken && hmacKeyB64) {
				sendToTerminal(
					"🔄 [SESSION_REINJECTED] Tentando reinicializar bootstrap após falha inicial",
					{},
					LogCategory.CONNECTION,
				);
				this.bootstrapInitialized = true;
			}

			return true;
		} catch (error) {
			sendToTerminal(
				"❌ [SESSION_REINJECTED] Erro ao atualizar sessão",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				LogCategory.ERROR,
			);
			return false;
		}
	}

	/**
	 * Verifica se o bootstrap foi inicializado com sucesso.
	 *
	 * @returns true se bootstrap foi inicializado, false caso contrário
	 */
	public isBootstrapInitialized(): boolean {
		return this.bootstrapInitialized;
	}

	/**
	 * Gera um requestId público (para testes)
	 */
	public generateRequestId(): string {
		return this.messageSender.generateRequestId();
	}

	/**
	 * Verifica se WebView está disponível (para testes)
	 */
	public isWebViewAvailable(): boolean {
		return this.messageValidator.isWebViewAvailable();
	}

	/**
	 * Retorna diagnóstico completo do estado da comunicação
	 *
	 * ✅ NOVO (2025): Útil para debugging em produção via console
	 * Uso: window.corelCommunication.getDiagnostics()
	 */
	public getDiagnostics(): {
		hasWebView: boolean;
		hasSessionToken: boolean;
		hasHmacSecret: boolean;
		tokenValid: boolean;
		bootstrapInitialized: boolean;
		queueSize: number;
		canSendMessages: boolean;
		timestamp: number;
		recommendations: string[];
	} {
		const token = this.getEffectiveToken();
		const hasHmacSecret = this.authManager.isHmacSecretAvailable();
		const hasWebView = this.messageValidator.isWebViewAvailable();
		const tokenValid = token ? this.isValidSessionToken(token) : false;
		const queueSize = this.messageQueue.size();

		const recommendations: string[] = [];

		if (!hasWebView) {
			recommendations.push(
				"❌ WebView2 não disponível - execute dentro do CorelDRAW",
			);
		}
		if (!token) {
			recommendations.push(
				"⚠️ sessionToken ausente - aguardando C# enviar bootstrap",
			);
		} else if (!tokenValid) {
			recommendations.push("⚠️ sessionToken inválido - formato incorreto");
		}
		if (!hasHmacSecret) {
			recommendations.push(
				"⚠️ HMAC secret ausente - aguardando C# enviar bootstrap",
			);
		}
		if (queueSize > 0) {
			recommendations.push(
				`📥 ${queueSize} mensagens na fila aguardando credenciais`,
			);
		}
		if (hasWebView && tokenValid && hasHmacSecret && queueSize === 0) {
			recommendations.push("✅ Sistema pronto para comunicação");
		}

		return {
			hasWebView,
			hasSessionToken: !!token,
			hasHmacSecret,
			tokenValid,
			bootstrapInitialized: this.bootstrapInitialized,
			queueSize,
			canSendMessages: hasWebView && tokenValid && hasHmacSecret,
			timestamp: Date.now(),
			recommendations,
		};
	}

	/**
	 * Força tentativa de flush da fila (público para debugging)
	 */
	public forceFlushQueue(): void {
		sendToTerminal(
			"🔄 [MANUAL] Forçando flush da fila",
			{},
			LogCategory.CONNECTION,
		);
		this.flushPendingQueue();
	}
}

// Exporta a instância única
export const corelCommunication = CorelCommunication.getInstance();
